/**
 * Resolves a `?return_to=` destination on another TeamTable host, for apps that send users here to
 * sign in (the analytics dashboard).
 *
 * Strictly allowlisted. This value is acted on immediately after a session cookie is issued, so an
 * unvalidated destination would be an open redirect that hands a freshly signed-in user — and
 * anything their browser sends — to whatever host an attacker put in the link.
 *
 * Rules: absolute URL, `https`, and a host that is `teamtable.co` or a subdomain of it. `http` is
 * allowed only for localhost so local dev works. Anything else resolves to `null`, and the caller
 * falls back to its normal in-app destination.
 */
export function resolveExternalReturnTo(search: string): string | null {
  return validateReturnTo(new URLSearchParams(search).get("return_to"));
}

/** Key holding the pending destination across the emailed-link hop. */
const RETURN_TO_STORAGE_KEY = "tt_admin_return_to";

/**
 * Remembers where to go after sign-in. The emailed link lands on `/auth/verify` in what is usually
 * a fresh tab, so the query parameter is gone by then; `localStorage` survives that, where
 * `sessionStorage` would not.
 */
export function rememberReturnTo(url: string): void {
  try {
    localStorage.setItem(RETURN_TO_STORAGE_KEY, url);
  } catch {
    /** Private mode or a full quota: sign-in still works, the user just lands on the dashboard. */
  }
}

/** Reads and clears the remembered destination, re-validating it rather than trusting storage. */
export function takeRememberedReturnTo(): string | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(RETURN_TO_STORAGE_KEY);
    localStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    return null;
  }
  return validateReturnTo(raw);
}

export function validateReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    /** Relative paths are handled by the portal's own router, not here. */
    return null;
  }

  const host = url.hostname.toLowerCase();
  const isLocalhost = host === "localhost" || host === "127.0.0.1" || host === "[::1]";

  if (url.protocol === "https:") {
    if (host !== "teamtable.co" && !host.endsWith(".teamtable.co")) return null;
  } else if (url.protocol === "http:") {
    if (!isLocalhost) return null;
  } else {
    /** Blocks `javascript:`, `data:` and anything else that is not a web fetch. */
    return null;
  }

  /**
   * Re-serialised from the parsed URL rather than passed through, so only the components `URL`
   * recognised survive. Credentials in the authority are dropped: `https://evil@analytics…` must
   * not reach `location.assign`.
   */
  url.username = "";
  url.password = "";
  return url.toString();
}
